import { ComponentType, FC, useCallback, useEffect, useState } from "react";
import { Slide, SlideProps, Snackbar, SnackbarOrigin } from "@mui/material";

import { Alert } from "../Alert";

type TransitionProps = Omit<SlideProps, "direction">;
type DirectionProps = "up" | "right" | "down" | "left";
type SeverityProps = "error" | "info" | "success" | "warning";
type PositionProps = SnackbarOrigin;

interface ToastMessageProps {
  open: boolean;
  message: string;
  direction?: DirectionProps;
  severity: SeverityProps;
  position?: PositionProps;
  autoHideDuration?: number;
  variant?: "filled" | "outlined" | "standard";
  tools?: JSX.Element;
  dataTestId?: string;

  onClose: () => void;
}

const ToastMessage: FC<ToastMessageProps> = ({
  direction = "left",
  open,
  message = "Toast message",
  severity,
  variant = "outlined",
  position = { horizontal: "right", vertical: "bottom" },
  autoHideDuration = 3000,
  dataTestId,
  tools,
  onClose,
}) => {
  const [transition, setTransition] = useState<
    ComponentType<TransitionProps> | undefined
  >(undefined);

  const TransitionComponent = useCallback(
    (props: TransitionProps) => <Slide {...props} direction={direction} />,
    [direction]
  );

  useEffect(() => {
    if (direction) {
      setTransition(() => TransitionComponent);
    }
  }, [TransitionComponent, direction]);

  return (
    <Snackbar
      open={open}
      anchorOrigin={position}
      onClose={onClose}
      TransitionComponent={transition}
      autoHideDuration={autoHideDuration}
      data-testid={dataTestId}
    >
      <Alert
        text={message}
        severity={severity}
        onClose={onClose}
        withClose
        variant={variant}
        tools={tools}
        dataTestId={dataTestId && `${dataTestId}-alert`}
      />
    </Snackbar>
  );
};

export default ToastMessage;
